'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { ArrowRight, Sparkles, ShieldCheck, TrendingUp, Zap, CheckCircle2 } from 'lucide-react';

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    canvas.width = width;
    canvas.height = height;

    // Particles
    const particles: { x: number; y: number; vx: number; vy: number; size: number; opacity: number; color: string }[] = [];
    const colors = ['#1A56DB', '#E8A020', '#60A5FA', '#F5C842', '#D4006A'];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2.5 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    // Floating geometric shapes (construction-inspired: triangles, squares)
    const shapes: { x: number; y: number; vx: number; vy: number; size: number; rotation: number; rotSpeed: number; type: 'triangle' | 'square' | 'hex'; opacity: number; color: string }[] = [];
    for (let i = 0; i < 18; i++) {
      shapes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        size: Math.random() * 18 + 8,
        rotation: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.008,
        type: (['triangle', 'square', 'hex'] as const)[Math.floor(Math.random() * 3)],
        opacity: Math.random() * 0.12 + 0.04,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    let tick = 0;

    function drawShape(ctx: CanvasRenderingContext2D, shape: typeof shapes[0]) {
      ctx.save();
      ctx.translate(shape.x, shape.y);
      ctx.rotate(shape.rotation);
      ctx.globalAlpha = shape.opacity;
      ctx.strokeStyle = shape.color;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      if (shape.type === 'triangle') {
        const s = shape.size;
        ctx.moveTo(0, -s);
        ctx.lineTo(s * 0.866, s * 0.5);
        ctx.lineTo(-s * 0.866, s * 0.5);
        ctx.closePath();
      } else if (shape.type === 'square') {
        const s = shape.size;
        ctx.rect(-s / 2, -s / 2, s, s);
      } else {
        // hexagon
        const s = shape.size;
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i;
          if (i === 0) ctx.moveTo(s * Math.cos(angle), s * Math.sin(angle));
          else ctx.lineTo(s * Math.cos(angle), s * Math.sin(angle));
        }
        ctx.closePath();
      }
      ctx.stroke();
      ctx.restore();
    }

    function drawBlueprintGrid(ctx: CanvasRenderingContext2D) {
      // Animated blueprint grid lines
      const gridSize = 60;
      const offset = (tick * 0.15) % gridSize;
      ctx.save();
      ctx.globalAlpha = 0.04;
      ctx.strokeStyle = '#60A5FA';
      ctx.lineWidth = 0.8;
      // Vertical lines
      for (let x = -gridSize + offset; x < width + gridSize; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      // Horizontal lines
      for (let y = -gridSize + offset; y < height + gridSize; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      ctx.restore();
    }

    function drawCrane(ctx: CanvasRenderingContext2D) {
      // Animated crane silhouette on the right side
      const cx = width * 0.88;
      const cy = height * 0.72;
      const swing = Math.sin(tick * 0.008) * 12;

      ctx.save();
      ctx.globalAlpha = 0.07;
      ctx.strokeStyle = '#E8A020';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';

      // Tower
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx, cy - 180);
      ctx.stroke();

      // Horizontal boom
      ctx.beginPath();
      ctx.moveTo(cx - 20, cy - 175);
      ctx.lineTo(cx + 100 + swing, cy - 175);
      ctx.stroke();

      // Counter boom
      ctx.beginPath();
      ctx.moveTo(cx - 20, cy - 175);
      ctx.lineTo(cx - 60, cy - 175);
      ctx.stroke();

      // Cable from boom tip
      ctx.beginPath();
      ctx.moveTo(cx + 100 + swing, cy - 175);
      ctx.lineTo(cx + 100 + swing, cy - 130);
      ctx.stroke();

      // Hook box
      ctx.strokeRect(cx + 92 + swing, cy - 130, 16, 12);

      // Support cables
      ctx.globalAlpha = 0.05;
      ctx.beginPath();
      ctx.moveTo(cx, cy - 175);
      ctx.lineTo(cx + 100 + swing, cy - 175);
      ctx.stroke();

      // Base legs
      ctx.globalAlpha = 0.07;
      ctx.beginPath();
      ctx.moveTo(cx - 12, cy);
      ctx.lineTo(cx - 20, cy + 30);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx + 12, cy);
      ctx.lineTo(cx + 20, cy + 30);
      ctx.stroke();

      ctx.restore();
    }

    function drawBuildingSilhouettes(ctx: CanvasRenderingContext2D) {
      ctx.save();
      ctx.globalAlpha = 0.06;
      ctx.fillStyle = '#1A56DB';

      // Building 1
      ctx.fillRect(width * 0.05, height * 0.65, 40, height * 0.35);
      ctx.fillRect(width * 0.05 + 5, height * 0.55, 30, height * 0.1);
      // Windows
      ctx.globalAlpha = 0.04;
      ctx.fillStyle = '#60A5FA';
      for (let row = 0; row < 5; row++) {
        for (let col = 0; col < 2; col++) {
          ctx.fillRect(width * 0.05 + 6 + col * 14, height * 0.67 + row * 14, 8, 8);
        }
      }

      // Building 2
      ctx.globalAlpha = 0.05;
      ctx.fillStyle = '#1A56DB';
      ctx.fillRect(width * 0.12, height * 0.72, 30, height * 0.28);
      ctx.fillRect(width * 0.12 + 4, height * 0.62, 22, height * 0.1);

      ctx.restore();
    }

    function drawConnectingLines(ctx: CanvasRenderingContext2D) {
      // Draw faint lines connecting nearby particles
      ctx.save();
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.globalAlpha = (1 - dist / 100) * 0.08;
            ctx.strokeStyle = '#1A56DB';
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      ctx.restore();
    }

    function animate() {
      tick++;
      ctx.clearRect(0, 0, width, height);

      drawBlueprintGrid(ctx);
      drawBuildingSilhouettes(ctx);
      drawCrane(ctx);

      // Update & draw shapes
      for (const shape of shapes) {
        shape.x += shape.vx;
        shape.y += shape.vy;
        shape.rotation += shape.rotSpeed;
        if (shape.x < -50) shape.x = width + 50;
        if (shape.x > width + 50) shape.x = -50;
        if (shape.y < -50) shape.y = height + 50;
        if (shape.y > height + 50) shape.y = -50;
        drawShape(ctx, shape);
      }

      // Update & draw particles
      ctx.save();
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      drawConnectingLines(ctx);

      animationId = requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden gradient-hero-mesh">
      {/* Construction-themed animated canvas background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 0 }}
      />

      {/* Animated glowing rings - construction helmet inspired */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
        {/* Pulsing ring 1 */}
        <div
          className="absolute rounded-full"
          style={{
            width: 600,
            height: 600,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            border: '1px solid rgba(232,160,32,0.08)',
            animation: 'ringPulse 4s ease-in-out infinite',
          }}
        />
        {/* Pulsing ring 2 */}
        <div
          className="absolute rounded-full"
          style={{
            width: 900,
            height: 900,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            border: '1px solid rgba(26,86,219,0.06)',
            animation: 'ringPulse 4s ease-in-out infinite 1.3s',
          }}
        />
        {/* Pulsing ring 3 */}
        <div
          className="absolute rounded-full"
          style={{
            width: 1200,
            height: 1200,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            border: '1px solid rgba(212,0,106,0.04)',
            animation: 'ringPulse 4s ease-in-out infinite 2.6s',
          }}
        />

        {/* Sweeping light beam */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '60%',
            height: '100%',
            background: 'linear-gradient(105deg, transparent 40%, rgba(232,160,32,0.03) 50%, transparent 60%)',
            animation: 'sweepBeam 8s linear infinite',
          }}
        />

        {/* Corner blueprint markers */}
        {[
          { top: '8%', left: '4%' },
          { top: '8%', right: '4%' },
          { bottom: '8%', left: '4%' },
          { bottom: '8%', right: '4%' },
        ].map((pos, i) => (
          <div
            key={`marker-${i}`}
            className="absolute"
            style={{
              ...pos,
              width: 24,
              height: 24,
              opacity: 0.2,
              animation: `markerBlink 3s ease-in-out infinite ${i * 0.7}s`,
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="#E8A020" strokeWidth="1.5">
              {i === 0 && <><path d="M0 8 L0 0 L8 0" /></>}
              {i === 1 && <><path d="M24 8 L24 0 L16 0" /></>}
              {i === 2 && <><path d="M0 16 L0 24 L8 24" /></>}
              {i === 3 && <><path d="M24 16 L24 24 L16 24" /></>}
            </svg>
          </div>
        ))}

        {/* Floating measurement lines */}
        <div
          style={{
            position: 'absolute',
            bottom: '15%',
            left: '8%',
            width: 120,
            height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(232,160,32,0.3), transparent)',
            animation: 'measureLine 5s ease-in-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '20%',
            right: '10%',
            width: 80,
            height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(26,86,219,0.3), transparent)',
            animation: 'measureLine 5s ease-in-out infinite 2s',
          }}
        />
      </div>

      {/* Grid lines decoration */}
      <div className="absolute inset-0 grid-lines opacity-100" style={{ zIndex: 1 }} />

      {/* Radial glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ zIndex: 1, background: 'radial-gradient(circle, rgba(26,86,219,0.15) 0%, transparent 70%)' }} />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ zIndex: 1, background: 'radial-gradient(circle, rgba(212,0,106,0.10) 0%, transparent 70%)' }} />
      <div className="absolute top-1/2 right-1/3 w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ zIndex: 1, background: 'radial-gradient(circle, rgba(232,160,32,0.08) 0%, transparent 70%)' }} />

      <style>{`
        @keyframes ringPulse {
          0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.04); }
        }
        @keyframes sweepBeam {
          0% { left: -100%; }
          100% { left: 200%; }
        }
        @keyframes markerBlink {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.6; }
        }
        @keyframes measureLine {
          0%, 100% { opacity: 0.3; transform: scaleX(0.7); }
          50% { opacity: 1; transform: scaleX(1); }
        }
      `}</style>

      <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 w-full" style={{ zIndex: 2 }}>
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: Copy */}
          <div className="space-y-7">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide" style={{ background: 'rgba(26,86,219,0.15)', border: '1px solid rgba(26,86,219,0.3)', color: '#60A5FA' }}>
              <Zap size={12} fill="currentColor" />
              AI-Powered Construction OS · Indonesia
            </div>

            {/* Headline */}
            <div className="space-y-2">
              <h1 className="text-5xl sm:text-6xl font-extrabold leading-[1.06] tracking-tight text-balance" style={{ color: 'white', letterSpacing: '-0.03em' }}>
                Wujudkan Properti
                <br />
                <span style={{ background: 'linear-gradient(90deg, #E8A020 0%, #F5C842 50%, #E8A020 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Impian Anda
                </span>
              </h1>
              <h1 className="text-5xl sm:text-6xl font-extrabold leading-[1.06] tracking-tight" style={{ color: 'rgba(255,255,255,0.85)', letterSpacing: '-0.03em' }}>
                dengan Cerdas
              </h1>
            </div>

            <p className="text-lg leading-relaxed max-w-lg" style={{ color: 'rgba(255,255,255,0.65)' }}>
              Platform AI pertama di Indonesia yang mengelola seluruh siklus renovasi dan konstruksi — dari ide awal hingga serah terima. Transparan, terverifikasi, terdokumentasi.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/mulai-proyek-wizard" className="btn-accent text-base py-3.5 px-7 rounded-xl">
                Mulai Proyek Gratis
                <ArrowRight size={18} />
              </Link>
              <Link
                href="/sign-up-login"
                className="inline-flex items-center justify-center gap-2 text-base font-semibold py-3.5 px-7 rounded-xl transition-all duration-200"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.85)' }}
              >
                Lihat Demo
              </Link>
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap items-center gap-5 pt-1">
              {[
                { icon: <ShieldCheck size={14} />, text: '2.400+ Profesional Terverifikasi' },
                { icon: <TrendingUp size={14} />, text: 'Rp 1,2T+ Proyek Terkelola' },
              ]?.map((item, i) => (
                <div key={`trust-${i}`} className="flex items-center gap-1.5">
                  <span style={{ color: '#E8A020' }}>{item?.icon}</span>
                  <span className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{item?.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Dashboard Preview */}
          <div className="hidden lg:block">
            <div className="relative animate-float">
              {/* Main dashboard card */}
              <div className="rounded-2xl p-5 space-y-4" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.10)', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
                {/* Header */}
                <div className="flex items-center justify-between pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>Proyek Aktif</p>
                    <p className="text-base font-bold mt-0.5 text-white">Renovasi Rumah Cipete</p>
                  </div>
                  <span className="status-badge status-konstruksi">Konstruksi</span>
                </div>

                {/* Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'rgba(255,255,255,0.55)' }}>Progress Konstruksi</span>
                    <span className="font-bold text-white">67%</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                    <div className="h-full rounded-full" style={{ width: '67%', background: 'linear-gradient(90deg, #1A56DB 0%, #60A5FA 100%)' }} />
                  </div>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { label: 'Anggaran', value: 'Rp 485 Jt', sub: 'dari Rp 600 Jt' },
                    { label: 'Milestone', value: 'Plesteran', sub: '3 hari lagi' },
                    { label: 'Tim Aktif', value: '8 Orang', sub: 'di lapangan' },
                  ]?.map((item, i) => (
                    <div key={`stat-${i}`} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{item?.label}</p>
                      <p className="text-sm font-bold mt-0.5 text-white">{item?.value}</p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{item?.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Mini activity */}
                <div className="space-y-2 pt-1">
                  {[
                    { dot: '#1A56DB', text: 'Laporan harian dikirim supervisor' },
                    { dot: '#E8A020', text: 'Persetujuan termin 3 menunggu' },
                  ]?.map((item, i) => (
                    <div key={`act-${i}`} className="flex items-center gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: item?.dot }} />
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{item?.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating AI card */}
              <div className="absolute -bottom-5 -right-5 rounded-2xl p-4 w-60" style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 16px 40px rgba(13,27,42,0.25)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #1A56DB, #60A5FA)' }}>
                    <Sparkles size={14} style={{ color: 'white' }} />
                  </div>
                  <div>
                    <p className="text-xs font-bold" style={{ color: 'var(--foreground)' }}>AI Renovation Advisor</p>
                    <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>Estimasi RAB siap. Penghematan potensial <strong style={{ color: '#0E8A4A' }}>Rp 38 Jt</strong> teridentifikasi.</p>
                  </div>
                </div>
              </div>

              {/* Floating verified badge */}
              <div className="absolute -top-5 -left-5 rounded-2xl px-4 py-2.5 flex items-center gap-2" style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 8px 24px rgba(13,27,42,0.15)' }}>
                <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'var(--green-light)' }}>
                  <CheckCircle2 size={14} style={{ color: 'var(--green)' }} />
                </div>
                <span className="text-xs font-bold" style={{ color: 'var(--foreground)' }}>Kontraktor Terverifikasi</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-6 pt-10" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {[
            { value: '2.400+', label: 'Profesional Aktif', color: '#60A5FA' },
            { value: '8.700+', label: 'Proyek Selesai', color: '#E8A020' },
            { value: '34', label: 'Kota di Indonesia', color: '#D4006A' },
            { value: '98%', label: 'Kepuasan Klien', color: '#34D399' },
          ]?.map((stat, i) => (
            <div key={`stat-row-${i}`} className="text-center">
              <p className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: stat?.color, letterSpacing: '-0.03em' }}>{stat?.value}</p>
              <p className="text-sm mt-1.5 font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>{stat?.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}